import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { BRAND_NAME } from '../brand';

import type { ModeSuggestionDetailResponse } from '../api/domains';
import ModeLimitBlock from './ModeLimitBlock';
import type { ModeLimitDetail } from '../utils/modeLimitMessaging';

type ModePreviewProps = {
    loading: boolean;
    preview: ModeSuggestionDetailResponse | null;
    error: string | null;
    notFound: boolean;
    expired: boolean;
    approving: boolean;
    approveError: string | null;
    modeLimitDetail: ModeLimitDetail | null;
    rejecting: boolean;
    deferring: boolean;
    decisionError: string | null;
    manualWizardHref: string;
    onRetry: () => void;
    onApprove: () => void;
    onReject: () => void;
    onDefer: () => void;
};

const ModePreview: React.FC<ModePreviewProps> = ({
    loading,
    preview,
    error,
    notFound,
    expired,
    approving,
    approveError,
    modeLimitDetail,
    rejecting,
    deferring,
    decisionError,
    manualWizardHref,
    onRetry,
    onApprove,
    onReject,
    onDefer,
}) => {
    const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
    const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
    const [deferConfirmOpen, setDeferConfirmOpen] = useState(false);

    if (loading) {
        return (
            <Box
                className="flex items-center gap-3 py-6"
                data-testid="MODE_PREVIEW_LOADING"
                aria-live="polite"
            >
                <CircularProgress size={24} aria-label="Loading mode preview" />
                <Typography variant="body2" color="text.secondary">
                    Loading preview…
                </Typography>
            </Box>
        );
    }

    if (notFound || expired) {
        return (
            <Alert
                severity="info"
                data-testid={expired ? 'MODE_PREVIEW_EXPIRED' : 'MODE_PREVIEW_NOT_FOUND'}
                action={
                    <Button component={RouterLink} to={manualWizardHref} color="inherit" size="small">
                        Build manually
                    </Button>
                }
            >
                {expired
                    ? 'This mode suggestion has expired. You can build a mode manually instead.'
                    : 'This suggestion is no longer available.'}
            </Alert>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                data-testid="MODE_PREVIEW_ERROR"
                action={
                    <Button color="inherit" size="small" onClick={onRetry}>
                        Retry
                    </Button>
                }
            >
                {error}
            </Alert>
        );
    }

    if (!preview) {
        return null;
    }

    const canApproveOrReject =
        preview.status === 'pending_review' || preview.status === 'deferred';
    const canDefer = preview.status === 'pending_review';
    const actionsDisabled = approving || rejecting || deferring;
    const planImpact = preview.plan_impact;
    const isPromotion = preview.source === 'subdomain_promotion';
    const migrationPreview = preview.evidence.migration_preview ?? {};
    const migrationCount = migrationPreview.eligible_count ?? 0;
    const overrideSkippedCount = migrationPreview.override_skipped_count ?? 0;
    const maturitySummary =
        typeof preview.evidence.maturity_summary === 'string'
            ? preview.evidence.maturity_summary
            : null;
    const showPlanWarning =
        planImpact.would_exceed_after_approve || planImpact.custom_modes_remaining === 0;
    const fields = preview.definition.fields ?? [];
    const statuses = preview.definition.statuses ?? [];
    const rules = preview.definition.classification_rules ?? [];

    return (
        <Box className="space-y-6" data-testid="MODE_PREVIEW">
            <div>
                <p className="text-xs font-medium uppercase tracking-wider text-teal-400">
                    {isPromotion ? 'Subdomain promotion' : 'Review before creating'}
                </p>
                <h1 className="text-2xl font-bold text-theme mt-1">
                    {isPromotion ? 'Promote to full mode' : preview.definition.label}
                </h1>
                {isPromotion ? (
                    <p className="text-sm text-theme-secondary mt-1">
                        Mode: {preview.definition.label}
                    </p>
                ) : null}
                <p className="text-sm text-theme-secondary mt-2 max-w-2xl">
                    {isPromotion
                        ? 'Review the proposed mode and how your grouped General items will move. Nothing changes until you confirm.'
                        : 'Nothing is saved as a mode until you confirm. Review the fields, routing rules, and plan impact below.'}
                </p>
                {preview.evidence.summary ? (
                    <p className="text-sm text-theme-secondary mt-3 max-w-2xl">
                        {preview.evidence.summary}
                    </p>
                ) : null}
                {isPromotion && (migrationCount > 0 || overrideSkippedCount > 0) ? (
                    <p className="text-sm text-theme mt-3 max-w-2xl" data-testid="MODE_PREVIEW_MIGRATION_CALLOUT">
                        {migrationCount > 0 ? (
                            <>
                                {migrationCount} item{migrationCount === 1 ? '' : 's'} will move from General to{' '}
                                {preview.definition.label} when you approve.
                            </>
                        ) : (
                            <>No items will move from General when you approve.</>
                        )}
                        {overrideSkippedCount > 0
                            ? ` ${overrideSkippedCount} item${overrideSkippedCount === 1 ? '' : 's'} with domain overrides will stay in General.`
                            : ''}
                    </p>
                ) : null}
            </div>

            {isPromotion && maturitySummary ? (
                <Alert severity="info" data-testid="MODE_PREVIEW_MATURITY_PANEL">
                    {maturitySummary}
                </Alert>
            ) : null}

            {showPlanWarning ? (
                <Alert severity="warning" data-testid="MODE_PREVIEW_PLAN_WARNING">
                    {planImpact.custom_mode_limit === null
                        ? 'You are at your active custom-mode limit. Approving may be blocked until you free a slot.'
                        : `You are using ${planImpact.active_custom_modes} of ${planImpact.custom_mode_limit} custom mode slots. Approving may be blocked until you archive or deactivate a mode.`}
                </Alert>
            ) : (
                <Alert severity="info" data-testid="MODE_PREVIEW_PLAN_IMPACT">
                    {planImpact.custom_mode_limit === null
                        ? `You have ${planImpact.active_custom_modes} active custom mode${planImpact.active_custom_modes === 1 ? '' : 's'}.`
                        : `Plan slots: ${planImpact.active_custom_modes} of ${planImpact.custom_mode_limit} used (${planImpact.custom_modes_remaining ?? 0} remaining).`}
                </Alert>
            )}

            {fields.length > 0 ? (
                <section aria-labelledby="mode-preview-fields-heading">
                    <Typography id="mode-preview-fields-heading" variant="subtitle1" className="!font-semibold !text-theme">
                        Fields
                    </Typography>
                    <TableContainer className="mt-2 rounded-lg border border-theme">
                        <Table size="small" aria-label="Suggested mode fields">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Required</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fields.map((field) => (
                                    <TableRow key={field.name}>
                                        <TableCell>{field.label}</TableCell>
                                        <TableCell>{field.type}</TableCell>
                                        <TableCell>{field.required ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </section>
            ) : null}

            {statuses.length > 0 ? (
                <section aria-labelledby="mode-preview-statuses-heading">
                    <Typography id="mode-preview-statuses-heading" variant="subtitle1" className="!font-semibold !text-theme">
                        Statuses
                    </Typography>
                    <ul className="mt-2 list-disc pl-5 text-sm text-theme-secondary space-y-1">
                        {statuses.map((status) => (
                            <li key={status.key}>
                                {status.label}
                                {status.terminal ? ' (terminal)' : ''}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            {rules.length > 0 ? (
                <section aria-labelledby="mode-preview-rules-heading">
                    <Typography id="mode-preview-rules-heading" variant="subtitle1" className="!font-semibold !text-theme">
                        Classification rules
                    </Typography>
                    <ul className="mt-2 list-disc pl-5 text-sm text-theme-secondary space-y-1">
                        {rules.map((rule, index) => (
                            <li key={`${rule.type}-${rule.pattern}-${index}`}>
                                {rule.type.replace(/_/g, ' ')}: {rule.pattern}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            {preview.examples.length > 0 ? (
                <section aria-labelledby="mode-preview-examples-heading">
                    <Typography id="mode-preview-examples-heading" variant="subtitle1" className="!font-semibold !text-theme">
                        Examples
                    </Typography>
                    <ul className="mt-2 space-y-3">
                        {preview.examples.map((example) => (
                            <li key={example.title} className="rounded-lg border border-theme px-3 py-2">
                                <div className="text-sm font-medium text-theme">{example.title}</div>
                                <div className="text-sm text-theme-secondary mt-1">{example.description}</div>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            {modeLimitDetail ? (
                <ModeLimitBlock detail={modeLimitDetail} context="create" />
            ) : null}

            {approveError ? (
                <Alert severity="error" role="alert" aria-live="polite">
                    {approveError}
                </Alert>
            ) : null}
            {decisionError ? (
                <Alert severity="error" role="alert" aria-live="polite">
                    {decisionError}
                </Alert>
            ) : null}

            <div className="flex flex-wrap gap-3" aria-live="polite">
                <Button
                    variant="contained"
                    color="primary"
                    disabled={actionsDisabled || !canApproveOrReject}
                    onClick={() => setApproveConfirmOpen(true)}
                    data-testid="MODE_PREVIEW_APPROVE"
                >
                    {approving
                        ? isPromotion
                            ? 'Promoting…'
                            : 'Creating mode…'
                        : isPromotion
                          ? 'Promote to full mode'
                          : 'Create mode'}
                </Button>
                <Button
                    variant="outlined"
                    disabled={actionsDisabled || !canApproveOrReject}
                    onClick={() => setRejectConfirmOpen(true)}
                    data-testid="MODE_PREVIEW_REJECT"
                >
                    {rejecting ? 'Rejecting…' : 'Reject'}
                </Button>
                <Button
                    variant="text"
                    disabled={actionsDisabled || !canDefer}
                    onClick={() => setDeferConfirmOpen(true)}
                    data-testid="MODE_PREVIEW_DEFER"
                >
                    {deferring ? 'Deferring…' : 'Decide later'}
                </Button>
                {!isPromotion ? (
                    <Link component={RouterLink} to={manualWizardHref} className="ml-auto self-center text-sm">
                        Build manually instead
                    </Link>
                ) : null}
            </div>

            <Dialog open={approveConfirmOpen} onClose={() => setApproveConfirmOpen(false)}>
                <DialogTitle>{isPromotion ? 'Promote to full mode?' : 'Create this mode?'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {isPromotion ? (
                            migrationCount > 0 ? (
                                <>
                                    {BRAND_NAME} will create &ldquo;{preview.definition.label}&rdquo; and move{' '}
                                    {migrationCount} item{migrationCount === 1 ? '' : 's'} from General into this
                                    mode.
                                    {overrideSkippedCount > 0
                                        ? ` ${overrideSkippedCount} item${overrideSkippedCount === 1 ? '' : 's'} with domain overrides will stay in General.`
                                        : ''}
                                </>
                            ) : (
                                <>
                                    {BRAND_NAME} will create &ldquo;{preview.definition.label}&rdquo;, but no items will
                                    move from General.
                                    {overrideSkippedCount > 0
                                        ? ` ${overrideSkippedCount} item${overrideSkippedCount === 1 ? '' : 's'} with domain overrides will stay in General.`
                                        : ''}
                                </>
                            )
                        ) : (
                            <>
                                {BRAND_NAME} will create &ldquo;{preview.definition.label}&rdquo; as a custom mode.
                                {planImpact.custom_mode_limit !== null
                                    ? ` This will use one of your ${planImpact.custom_mode_limit} custom mode slots.`
                                    : ''}
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveConfirmOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setApproveConfirmOpen(false);
                            onApprove();
                        }}
                        autoFocus
                    >
                        {isPromotion ? 'Promote' : 'Create mode'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectConfirmOpen} onClose={() => setRejectConfirmOpen(false)}>
                <DialogTitle>Reject this suggestion?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The suggestion will be marked rejected. No mode is created and your existing
                        work items are not affected.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectConfirmOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        onClick={() => {
                            setRejectConfirmOpen(false);
                            onReject();
                        }}
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deferConfirmOpen} onClose={() => setDeferConfirmOpen(false)}>
                <DialogTitle>Decide later?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You can return to this suggestion later. Nothing is saved as a mode yet.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeferConfirmOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => {
                            setDeferConfirmOpen(false);
                            onDefer();
                        }}
                    >
                        Defer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ModePreview;
